import * as React from 'react';

import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { UIMenus } from '@webapp/store/ui/types';
import { OpenMenu, CloseMenu } from '@webapp/store/ui/action-creators';

import styled, { css } from 'styled-components';

import { motion } from 'framer-motion';

import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import { easeTransition } from '@components/framer';

import CloseIcon from '@components/basics/icons/close';

interface MenuProps {
  className?: string;
  children?: any;
  width?: string;
  minWidth?: string;
  bgImg?: string;
  button?: React.ReactNode;
  closeButton?: React.ReactNode;
  title?: string;
  customIconHeight?: string;
  alignCenter?: boolean;
  hasClose?: boolean;
  hasPaddingBottom?: boolean;
  hasPaddingTop?: string;
  isAddLinkTracpage?: boolean;
  parentRelative?: boolean;
  menuOpened?: UIMenus;
  menuId: UIMenus;
  isMobile?: boolean;
  openMenu: (menuId: UIMenus) => OpenMenu;
  closeMenu: () => CloseMenu;
  onClick?: () => void;
  customCloseMenu?: React.ReactNode;
}

const mapStateToProps = (state: AppState) => ({
  menuOpened: state.ui.menuOpened,
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openMenu: (menuId: UIMenus) => dispatch(new OpenMenu(menuId)),
  closeMenu: () => dispatch(new CloseMenu()),
});

const ARROW_SIZE = '30px';

export const Menu: React.FC<MenuProps> = (props) => {
  const isOpened = props.menuOpened === props.menuId;
  const [showMenu, setShowMenu] = React.useState(isOpened);

  const elementRef = React.useRef<HTMLDivElement>(null);

  const handleClickAway = (event: MouseEvent) => {
    if (
      elementRef.current &&
      !elementRef.current.contains(event.target as Node)
    ) {
      setShowMenu(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickAway);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, []);

  const ButtonOpen = props.button ? (
    props.button
  ) : (
    <Button title={props.title} />
  );
  const ButtonClose = props.closeButton ? props.closeButton : ButtonOpen;

  const opacity = {
    visible: {
      opacity: 1,
      scale: 1,
    },
    hidden: {
      opacity: 0,
      scale: 0.975,
    },
  };

  const handleCLick = () => {
    if (props.onClick) {
      props.onClick();
      setShowMenu(!showMenu);
    } else {
      setShowMenu(!showMenu);
    }
  };


  return (
    <div
      className={`${props.className} ${
        props.parentRelative ? 'relative' : ''
      } ${showMenu ? 'parent-opened' : ''}`}
    >
      <div
        className={`menu-action ${showMenu ? 'opened' : ''}`}
        onClick={() => handleCLick()}
      >
        {showMenu ? ButtonClose : ButtonOpen}
      </div>
      <motion.div
        ref={elementRef}
        className={`menu-items-container ${showMenu ? 'opened' : ''}`}
        initial="hidden"
        animate={showMenu ? 'visible' : 'hidden'}
        exit="hidden"
        variants={opacity}
        transition={{ ...easeTransition, duration: 0.2 }}
      >
        <Flex justifyContent="space-between" className="menu-content">
          {props.customCloseMenu ? (
            <Flex
              className="close"
              alignItems="center"
              justifyContent="space-between"
            >
              {props.customCloseMenu}
              <CloseIcon onClick={() => props.closeMenu()} />
            </Flex>
          ) : null}
          {props.hasClose ? (
            <Flex
              className="close"
              alignItems="flex-end"
              justifyContent="flex-end"
            >
              <CloseIcon onClick={() => props.closeMenu()} />
            </Flex>
          ) : null}
          {props.children}
        </Flex>
      </motion.div>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(Menu)`
  z-index: ${({ theme }) => theme.zIndices.over};

  &.parent-opened {
    z-index: ${({ theme }) => theme.zIndices.menus};
  }

  &.relative {
    position: relative;
  }

  @media ${({ theme }) => theme.device.tablet} {
    position: relative;
  }

  .menu-action {
    position: relative;

    &.opened {
      z-index: ${({ theme }) => theme.zIndices.under};
      .menu-items-container {
        z-index: ${({ theme }) => theme.zIndices.over};
      }
    }

    ${(props) =>
      props.customIconHeight
        ? css`
            height: ${props.customIconHeight};
            width: ${props.customIconHeight};
          `
        : null};
  }

  .menu-items-container {
    position: absolute;
    right: 0;
    pointer-events: none;
    box-sizing: border-box;
    width: auto;
    filter: drop-shadow(
      ${({ theme }) => `${theme.general.boxShadowMenu} ${theme.colors.shadow}`}
    );

    ${(props) =>
      props.hasPaddingTop
        ? css`
            margin-top: 10px;
          `
        : css`
            top: 100%;
          `}
    ${(props) =>
      props.isAddLinkTracpage
        ? css`
            left: auto;
          `
        : css`
            right: 0;
            @media ${({ theme }) => theme.device.tablet} {
              left: 0;
              right: auto;
            }
          `}
    ${(props) =>
      props.hasPaddingTop
        ? css`
            width: 15rem !important;
          `
        : null}

${(props) =>
      props.customIconHeight
        ? css`
            top: calc(100% + (${props.customIconHeight} / 6));
            @media ${({ theme }) => theme.device.tablet} {
              top: 100%;
            }
          `
        : null};

    &.opened {
      pointer-events: initial;
      z-index: ${({ theme }) => theme.zIndices.modal};
    }

    @media ${({ theme }) => theme.device.tablet} {
      top: ${(props) =>
        props.customIconHeight
          ? `calc(${props.customIconHeight} + 12px)`
          : '32px'};
      right: 0;
      left: auto;
      border-radius: 10px;
      width: ${(props) => (props.width ? props.width : 'auto')};

      ${(props) =>
        props.minWidth
          ? css`
              min-width: ${props.minWidth};
            `
          : null}
      ${(props) =>
        props.customIconHeight
          ? css`
              right: calc(-${props.customIconHeight} / 4);
            `
          : null};
    }

    @media ${({ theme }) => theme.device.tablet} {
      &:before {
        content: '';
        height: ${ARROW_SIZE};
        width: ${ARROW_SIZE};
        background-color: ${({ theme }) => theme.colors.menuBackground};
        position: absolute;
        right: 4px;
        top: 70px;
        transform: rotate(45deg);
        ${(props) =>
          props.hasPaddingTop
            ? css`
                border-radius: 0px;
              `
            : css`
                border-radius: 4px 0 0 0;
              `}
        z-index: 0;

        ${(props) =>
          props.customIconHeight
            ? css`
                top: 0;
              `
            : null};
      }
    }
  }

  &.left-aligned-link-dropdown {
    .menu-items-container {
      left: 0;
      right: auto;
      @media ${({ theme }) => theme.device.tablet} {
        left: 0;
        right: auto;
      }
    }
  }
  .menu-content {
    background-color: ${({ theme }) => theme.colors.menuBackground};
    position: relative;

    ${(props) =>
      props.hasPaddingTop
        ? css`
            padding: 0px;
          `
        : css`
            padding: 12px;
          `}
    z-index: 1;
    flex-direction: column;

    @media ${({ theme }) => theme.device.tablet} {
      background-color: ${({ theme }) => theme.colors.menuBackground};

      ${(props) =>
        props.hasPaddingTop
          ? css`
              padding: 0px;
            `
          : css`
              padding: 20px;
            `}
      ${(props) =>
        props.hasPaddingTop
          ? css`
              border-radius: 0px;
            `
          : css`
              border-radius: 10px;
            `}
      flex-direction: row;
    }

    .close {
      margin-bottom: 30px;
    }

    ${(props) =>
      props.bgImg
        ? css`
            background-image: ${props.bgImg};
            background-position: bottom left;
            background-repeat: no-repeat;
          `
        : null}

    ${(props) =>
      props.hasPaddingBottom
        ? css`
            padding-bottom: 110px;
          `
        : null}
  }
`);
